import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Mail,
  Users,
  ScrollText,
  CheckCircle2,
  CircleDot,
  MinusCircle,
  Sparkles
} from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import sbcLogo from "@/assets/sbc-logo-light-canvas-1200x400.png";

/*
  40-credit landing-page + commercial-gate for the existing intake-snapshot
  app. This is a lightweight, presentation-only file:
    * new landing content at "/"
    * routes intake / results / report / admin all remain unchanged
    * no backend, DB, auth, payments, quote-builder or admin file touched
  Copy in this file follows the SBC governance vocabulary — no "compliant",
  "pass/fail", "certified", "audit-ready" or "guarantee" language outside
  approved negation clauses.
*/

const CONTACT_EMAIL = "hello@spiralbeyondcompliance.com.au";
const CONTACT_MAILTO =
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("SIL readiness — Spiral Beyond Compliance")}`;

const BOUNDARY_LINES = [
  "Spiral Beyond Compliance provides consultancy, readiness and operational governance support.",
  "The initial intake is preliminary only.",
  "This is not legal advice.",
  "This is not audit certification.",
  "This is not an NDIS Commission tool.",
  "This is not a guarantee of NDIS registration.",
  "This is not a guarantee of audit success.",
  "This is not a guarantee of compliance approval.",
  "Spiral Beyond Compliance is not an Approved Quality Auditor.",
  "Detailed review requires direct engagement with Spiral Beyond Compliance.",
  "Do not submit participant-identifying information unless a formal engagement and privacy process is in place."
];

const FOOTER_DISCLAIMER =
  "Spiral Beyond Compliance provides consultancy, readiness and operational governance support. Information on this website is general in nature and does not constitute legal, financial, clinical, audit or regulatory advice. Spiral Beyond Compliance is not an Approved Quality Auditor and does not guarantee NDIS registration, audit success or compliance approval. The public intake provides a preliminary readiness signal only. Detailed review and provider-specific advice require a separate paid engagement.";

// Premium hero visual — a stylised preview of the readiness snapshot
// output. Layered document cards with real-looking signal bars, evidence
// chips and a paper texture. This is the "product screenshot" moment.
function ReadinessSnapshotPreview() {
  const domains = [
    { code: "R", label: "Rights & governance", state: "amber", pct: 62 },
    { code: "E", label: "Evidence base", state: "red", pct: 34 },
    { code: "A", label: "Assurance & oversight", state: "amber", pct: 58 },
    { code: "D", label: "Delivery integrity", state: "green", pct: 78 },
    { code: "Y", label: "Yielding to review", state: "amber", pct: 51 }
  ];

  const stateStyles = {
    green: { bar: "bg-emerald-500/85", pill: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30", dot: "bg-emerald-500" },
    amber: { bar: "bg-amber-500/85", pill: "bg-amber-500/10 text-amber-700 border-amber-500/30", dot: "bg-amber-500" },
    red:   { bar: "bg-rose-500/85",   pill: "bg-rose-500/10 text-rose-700 border-rose-500/30",     dot: "bg-rose-500" }
  };

  return (
    <div className="relative min-w-0 w-full max-w-full">
      {/* Ambient wash */}
      <div className="pointer-events-none absolute -inset-6 rounded-[24px] bg-gradient-to-br from-accent/10 via-transparent to-transparent blur-2xl" aria-hidden="true" />

      {/* Rear stacked card (offset for depth) */}
      <div
        className="pointer-events-none absolute -right-3 top-6 hidden h-[92%] w-[92%] rounded-md border border-border/70 bg-card/70 shadow-paper sm:block"
        style={{ transform: "rotate(1.4deg)" }}
        aria-hidden="true"
      />
      {/* Second rear card */}
      <div
        className="pointer-events-none absolute -left-2 top-3 hidden h-[94%] w-[94%] rounded-md border border-border/70 bg-card/80 shadow-paper sm:block"
        style={{ transform: "rotate(-0.8deg)" }}
        aria-hidden="true"
      />

      {/* Main document */}
      <div className="relative min-w-0 w-full overflow-hidden rounded-md border border-border bg-card shadow-elevated">
        {/* Subtle grid backdrop */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.35]"
        >
          <defs>
            <pattern id="snapshot-grid" width="22" height="22" patternUnits="userSpaceOnUse">
              <path d="M 22 0 L 0 0 0 22" fill="none" stroke="currentColor" strokeOpacity="0.06" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#snapshot-grid)" className="text-foreground" />
        </svg>

        {/* Header */}
        <div className="relative border-b border-border/80 bg-gradient-to-b from-background/60 to-transparent px-5 pb-4 pt-5">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  NDIS-0138 · READY
                </span>
              </span>
              <span className="shrink-0 font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Preview
              </span>
            </div>
            <span className="shrink-0 whitespace-nowrap font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              REF · SBC-SIL-0247
            </span>
          </div>
          <p className="mt-3 font-display text-[17px] leading-tight text-foreground">
            Preliminary readiness snapshot
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
            Supported Independent Living · 24 staff · registered provider
          </p>
        </div>

        {/* R.E.A.D.Y. domain signal bars */}
        <div className="relative px-5 py-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              § 05 · R.E.A.D.Y. summary
            </p>
            <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              5 domains
            </p>
          </div>
          <ul className="space-y-2.5">
            {domains.map((d) => {
              const s = stateStyles[d.state];
              return (
                <li key={d.code} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border border-border bg-background font-display text-[11px] font-medium text-foreground">
                    {d.code}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[12px] text-foreground/90">{d.label}</p>
                      <span className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {d.pct}%
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${s.bar}`}
                        style={{ width: `${d.pct}%` }}
                      />
                    </div>
                  </div>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${s.dot}`} aria-hidden="true" />
                </li>
              );
            })}
          </ul>
        </div>

        {/* Evidence gap chips */}
        <div className="relative border-t border-border/80 px-5 py-4">
          <div className="mb-2 flex items-center gap-2">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              § 06 · Evidence gaps surfaced
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { t: "Restrictive practice records", s: "red" },
              { t: "Incident escalation trail", s: "red" },
              { t: "Supervision cadence", s: "amber" },
              { t: "Medication oversight", s: "amber" },
              { t: "Complaints register", s: "green" }
            ].map((c) => {
              const s = stateStyles[c.s];
              const Icon = c.s === "red" ? MinusCircle : c.s === "amber" ? CircleDot : CheckCircle2;
              return (
                <span
                  key={c.t}
                  className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] ${s.pill}`}
                >
                  <Icon className="h-3 w-3" />
                  {c.t}
                </span>
              );
            })}
          </div>
        </div>

        {/* Next-step callout */}
        <div className="relative flex items-start gap-3 border-t border-border/80 bg-gradient-to-br from-accent/5 to-transparent px-5 py-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-accent/30 bg-background text-accent">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Recommended next step
            </p>
            <p className="mt-0.5 text-[12px] leading-relaxed text-foreground/90">
              This is a preview of the kind of structured snapshot you receive after the check.
            </p>
          </div>
        </div>

        {/* Footer meta */}
        <div className="relative grid grid-cols-3 divide-x divide-border border-t border-border/80 bg-muted/30">
          {[
            { k: "0138", label: "Signal set" },
            { k: "SIL", label: "Focus" },
            { k: "0", label: "Identifiers asked" }
          ].map((s) => (
            <div key={s.label} className="min-w-0 px-2 py-3 text-center sm:px-3">
              <div className="font-display text-[15px] font-medium text-foreground">{s.k}</div>
              <div className="mt-0.5 font-mono-label text-[10px] uppercase leading-tight tracking-[0.14em] text-muted-foreground">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating annotation chip */}
      <div
        className="absolute -bottom-3 left-6 hidden items-center gap-2 rounded-sm border border-border bg-card px-3 py-2 shadow-paper sm:flex"
        aria-hidden="true"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Preview · not a real provider
        </span>
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8 max-w-3xl">
      {eyebrow && (
        <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-2 font-display text-3xl leading-tight text-foreground sm:text-4xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-x-clip border-b border-border bg-gradient-paper">
      <div className="container grid grid-cols-1 items-start gap-12 py-16 md:py-20 lg:grid-cols-2 lg:gap-10 lg:py-24 xl:gap-12">
        <div className="flex min-w-0 flex-col gap-8">
          <div className="self-start">
            <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              NDIS 0138 Readiness · Supported Independent Living
            </span>
          </div>
          <div className="flex max-w-xl flex-col gap-3 lg:max-w-none">
            <h1 className="font-display text-3xl font-medium leading-[1.15] tracking-tight text-foreground sm:text-4xl lg:text-[2.75rem]">
              What the new SIL requirements mean for you.
            </h1>
            <p className="max-w-xl text-lg font-medium leading-snug text-foreground sm:text-xl">
              For Supported Independent Living providers navigating the new SIL Practice Standards and registration requirements.
            </p>
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground">
              Whether you already deliver SIL, are preparing to enter the market, or simply need clarity on the new requirements, this gives you a structured place to start.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <Button
                asChild
                size="lg"
                className="shrink-0 rounded-sm px-6 text-[13px] font-medium tracking-wide shadow-paper"
                data-testid="landing-primary-cta"
              >
                <Link to="/intake">
                  <span className="shrink-0">Check your SIL readiness</span>
                  <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                </Link>
              </Button>
              <a
                href={CONTACT_MAILTO}
                className="text-[13px] font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                data-testid="landing-secondary-cta"
              >
                Contact Spiral Beyond Compliance
              </a>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Start here without committing to any further work.
            </p>
          </div>
        </div>
        <div className="relative min-w-0 w-full lg:max-w-none">
          <ReadinessSnapshotPreview />
        </div>
      </div>
    </section>
  );
}

function ReadinessPropositionSection() {
  return (
    <section className="border-b border-border bg-background">
      <div className="container py-16">
        <SectionHeading
          eyebrow="NDIS 0138 Readiness"
          title="See how the new SIL requirements relate to your service."
        />
        <div className="max-w-3xl space-y-4">
          <p className="text-base leading-relaxed text-foreground/90 sm:text-lg">
            This short readiness check takes you through the new SIL Practice Standards and registration requirements in the context of how you currently operate.
          </p>
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            You’ll get a structured view of what you already have in place, where there may be gaps in evidence or governance, and what to look at next.
          </p>
          <p>
            <Link
              to="/intake"
              className="text-[13px] font-medium text-foreground underline-offset-4 hover:underline"
              data-testid="landing-proposition-cta"
            >
              Check your SIL readiness
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}

function BoundariesSection() {
  return (
    <section className="border-b border-border bg-muted/30">
      <div className="container py-16">
        <SectionHeading
          eyebrow="Clear boundaries"
          title="What this service is — and what it is not"
          subtitle="Every provider engagement operates inside these boundaries."
        />
        <ul
          className="grid grid-cols-1 gap-3 rounded-sm border border-border bg-card p-6 md:grid-cols-2"
          data-testid="landing-boundaries"
        >
          {BOUNDARY_LINES.map((line) => (
            <li key={line} className="flex items-start gap-2 text-sm text-foreground/90">
              <ScrollText className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section id="contact-john" className="border-b border-border bg-background">
      <div className="container py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Contact"
              title="Contact Spiral Beyond Compliance"
            />
            <div className="rounded-sm border border-border bg-card p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-sm border border-border bg-background">
                  <Users className="h-6 w-6 text-accent" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <p className="font-display text-lg text-foreground">John Kenneally</p>
                  <p className="text-sm text-muted-foreground">Spiral Beyond Compliance</p>
                  <a
                    href={CONTACT_MAILTO}
                    className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-accent underline-offset-4 hover:underline"
                    data-testid="landing-contact-email"
                  >
                    <Mail className="h-4 w-4" />
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild className="rounded-sm">
                  <a href={CONTACT_MAILTO} data-testid="landing-contact-cta">
                    Contact Spiral Beyond Compliance
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </Button>
                <Button asChild variant="outline" className="rounded-sm">
                  <Link to="/intake">Check your SIL readiness</Link>
                </Button>
              </div>
            </div>
          </div>
          <aside className="rounded-sm border border-border bg-card p-6 lg:col-span-5">
            <p className="eyebrow">What to include in your email</p>
            <ul className="mt-3 space-y-2 text-sm text-foreground/85">
              <li>• Whether you deliver Supported Independent Living</li>
              <li>• Approximate size of the operation (staff / participants)</li>
              <li>• Whether you are currently registered, unregistered or in-progress</li>
              <li>• The readiness question you want addressed first</li>
            </ul>
            <p className="mt-4 text-xs text-muted-foreground">
              Please do not include participant-identifying information until a formal engagement
              and privacy process is in place.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

function FooterDisclaimer() {
  return (
    <section
      className="border-b border-border bg-foreground/[0.02]"
      data-testid="landing-footer-disclaimer"
    >
      <div className="container py-10">
        <div className="flex items-start gap-4 rounded-sm border border-border bg-card p-5">
          <img
            src={sbcLogo}
            alt="Spiral Beyond Compliance"
            className="h-10 w-auto shrink-0 object-contain"
          />
          <div className="space-y-1">
            <p className="eyebrow text-foreground">Spiral Beyond Compliance</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {FOOTER_DISCLAIMER}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function NDIS0138LandingPage() {
  return (
    <PageShell>
      <div data-testid="ndis0138-landing">
        <Hero />
        <ReadinessPropositionSection />
        <ContactSection />
        <BoundariesSection />
        <FooterDisclaimer />
      </div>
    </PageShell>
  );
}
